/**
 * 航班实时轨迹地图 - 独立全屏页面
 *
 * 功能:
 * 1. 实时地图: 深色底图 + 大圆弧线 + 飞机图标
 * 2. 航段筛选: 全部 / 计划中 / 飞行中 / 已完成
 * 3. 日期切换: 默认今日实时，可切换历史日期查看
 * 4. 单段回放: 点击已完成航段 → 加载单段轨迹
 * 5. 整段回放: 加载同一 rotation 全部已完成航段 → 连续回放含经停站
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GeodesicLine } from "leaflet.geodesic";
import { screenService } from "../../api/enhancedApi";
import useInterval from "../../hooks/useInterval";
import "./FlightMapPage.css";

dayjs.extend(utc);
dayjs.extend(tz);

const REFRESH_INTERVAL = 30000;
const SPEED_OPTIONS = [0.5, 1, 2, 4, 8];

const FILTER_OPTIONS = [
  { value: "all", label: "全部航班", icon: "◈" },
  { value: "upcoming", label: "计划中", icon: "- -" },
  { value: "in_flight", label: "飞行中", icon: "✈" },
  { value: "completed", label: "已完成", icon: "—" },
];

// ─── Leaflet helpers ────────────────────────────────────────────────────────

function createAirportIcon() {
  return L.divIcon({
    className: "airport-marker",
    html: `<div class="airport-dot"></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  });
}

function createStopoverIcon() {
  return L.divIcon({
    className: "stopover-marker",
    html: `<div class="stopover-diamond"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function createPlaneIcon(heading, color, pulse = false) {
  return L.divIcon({
    className: "plane-marker",
    html: `<div class="plane-icon-wrapper" style="transform:rotate(${heading || 0}deg)">
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path d="M12 2L6 9h4v6H6l6 7 6-7h-4V9h4z" fill="${color}" stroke="#000" stroke-width="0.5"/>
      </svg>
    </div>
    ${pulse ? `<div class="plane-glow" style="box-shadow:0 0 12px 4px ${color}40"></div>` : ""}`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function createReplayPlaneIcon(heading, color) {
  return L.divIcon({
    className: "plane-marker",
    html: `<div class="plane-icon-wrapper" style="transform:rotate(${heading || 0}deg)">
      <svg viewBox="0 0 24 24" width="32" height="32">
        <path d="M12 2L6 9h4v6H6l6 7 6-7h-4V9h4z" fill="${color}" stroke="#fff" stroke-width="1"/>
      </svg>
    </div>
    <div class="replay-glow" style="border-color:${color}40"></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

// ─── 回放开始时自动 fitBounds 到轨迹范围 ─────────────────────────────────────

function ReplayFitBounds({ points }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!points?.length || fitted.current) return;
    const validPts = points.filter((p) => p.latitude && p.longitude);
    if (validPts.length < 2) return;
    const latLngs = validPts.map((p) => [p.latitude, p.longitude]);
    map.fitBounds(L.latLngBounds(latLngs), { padding: [60, 60], maxZoom: 8 });
    fitted.current = true;
  }, [map, points]);

  // 回放结束时重置，以便下次回放重新 fit
  useEffect(() => {
    fitted.current = false;
  }, [points]);

  return null;
}

// ─── Geodesic route lines (imperative) ──────────────────────────────────────

function GeodesicLinesLayer({ aircraft }) {
  const map = useMap();
  const linesRef = useRef([]);

  useEffect(() => {
    linesRef.current.forEach((l) => map.removeLayer(l));
    linesRef.current = [];

    aircraft.forEach((ac) => {
      ac.flights.forEach((flight) => {
        const dep = flight.departure_airport;
        const arr = flight.arrival_airport;
        if (!dep?.latitude || !dep?.longitude || !arr?.latitude || !arr?.longitude) return;
        const depLL = [dep.latitude, dep.longitude];
        const arrLL = [arr.latitude, arr.longitude];

        if (flight.segment_type === "completed") {
          const l = new GeodesicLine([depLL, arrLL], { weight: 2.5, opacity: 0.85, color: ac.color, steps: 50 });
          l.addTo(map);
          linesRef.current.push(l);
        } else if (flight.segment_type === "in_flight") {
          const pos = flight.current_position;
          if (pos?.latitude && pos?.longitude) {
            const posLL = [pos.latitude, pos.longitude];
            const s = new GeodesicLine([depLL, posLL], { weight: 2.5, opacity: 0.9, color: ac.color, steps: 50 });
            const d = new GeodesicLine([posLL, arrLL], { weight: 2, opacity: 0.45, color: ac.color, dashArray: "8,6", steps: 50 });
            s.addTo(map); d.addTo(map);
            linesRef.current.push(s, d);
          } else {
            const l = new GeodesicLine([depLL, arrLL], { weight: 2.5, opacity: 0.65, color: ac.color, dashArray: "12,6", steps: 50 });
            l.addTo(map);
            linesRef.current.push(l);
          }
        } else {
          const l = new GeodesicLine([depLL, arrLL], { weight: 1.5, opacity: 0.3, color: ac.color, dashArray: "5,9", steps: 50 });
          l.addTo(map);
          linesRef.current.push(l);
        }
      });
    });

    return () => { linesRef.current.forEach((l) => map.removeLayer(l)); linesRef.current = []; };
  }, [aircraft, map]);

  return null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatGround(minutes) {
  if (!minutes || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? m + "m" : ""}` : `${m}m`;
}

// ─── Main Component ─────────────────────────────────────────────────────────

const FlightMapPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 今日北京时间日期（YYYY-MM-DD），作为"实时"基准
  const todayBJ = dayjs().utcOffset(8).format("YYYY-MM-DD");

  // 当前查看日期：优先读 URL ?date=，默认今天
  const [selectedDate, setSelectedDate] = useState(
    searchParams.get("date") || todayBJ
  );
  const isToday = selectedDate === todayBJ;

  const [mapData, setMapData] = useState(null);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  const [segmentFilter, setSegmentFilter] = useState("all");
  const [selectedFlight, setSelectedFlight] = useState(null);

  // replay 状态扩展: 支持 stopovers + legs 用于整段回放
  const [replay, setReplay] = useState(null);
  const replayIntervalRef = useRef(null);

  // ── 数据获取 ──
  const fetchData = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const params = isToday ? {} : { date: selectedDate };
      const resp = await screenService.getFlightMap(params);
      setMapData(resp.data);
      setLastUpdate(dayjs());
    } catch (err) {
      console.error("[FlightMap] fetch failed:", err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [selectedDate, isToday]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // 历史日期不轮询（传 null 暂停定时器）
  useInterval(() => fetchData(), isToday ? REFRESH_INTERVAL : null, false);
  useInterval(() => setCurrentTime(dayjs()), 1000, true);

  // ── 派生数据 ──
  const aircraftList = useMemo(() => mapData?.aircraft || [], [mapData]);

  const filteredAircraftList = useMemo(() => {
    if (segmentFilter === "all") return aircraftList;
    return aircraftList
      .map((ac) => ({ ...ac, flights: ac.flights.filter((f) => f.segment_type === segmentFilter) }))
      .filter((ac) => ac.flights.length > 0);
  }, [aircraftList, segmentFilter]);

  const { airports, planes } = useMemo(() => {
    const airportMap = new Map();
    const planeList = [];
    filteredAircraftList.forEach((ac) => {
      ac.flights.forEach((f) => {
        [f.departure_airport, f.arrival_airport].forEach((apt) => {
          if (apt?.latitude && apt?.longitude && !airportMap.has(apt.iata)) airportMap.set(apt.iata, apt);
        });
        if (f.segment_type === "in_flight" && f.current_position?.latitude) {
          planeList.push({
            key: `${ac.registration}-${f.flight_number}`,
            registration: ac.registration, flightNumber: f.flight_number,
            color: ac.color, position: f.current_position, flight: f, aircraft: ac,
          });
        }
      });
    });
    return { airports: Array.from(airportMap.values()), planes: planeList };
  }, [filteredAircraftList]);

  const inFlightCount = useMemo(() => aircraftList.filter((ac) => ac.flights.some((f) => f.segment_type === "in_flight")).length, [aircraftList]);
  const completedCount = useMemo(() => aircraftList.reduce((n, ac) => n + ac.flights.filter((f) => f.segment_type === "completed").length, 0), [aircraftList]);
  const upcomingCount = useMemo(() => aircraftList.reduce((n, ac) => n + ac.flights.filter((f) => f.segment_type === "upcoming").length, 0), [aircraftList]);

  // ── 交互 ──
  const handlePlaneClick = useCallback((plane) => {
    setSelectedFlight({ ...plane.flight, registration: plane.registration, color: plane.color, aircraft_type: plane.aircraft.aircraft_type, icao24: plane.aircraft.icao24 });
  }, []);

  const handleFlightClick = useCallback((flight, ac) => {
    setSelectedFlight({ ...flight, registration: ac.registration, color: ac.color, aircraft_type: ac.aircraft_type, icao24: ac.icao24 });
  }, []);

  const handleAircraftSelect = useCallback((ac) => {
    const matchFilter = segmentFilter !== "all" ? ac.flights.find((f) => f.segment_type === segmentFilter) : null;
    const inFlight = ac.flights.find((f) => f.segment_type === "in_flight");
    const pick = matchFilter || inFlight || ac.flights[0];
    if (pick) handleFlightClick(pick, ac);
  }, [handleFlightClick, segmentFilter]);

  // ── 回放：停止 ──
  const stopReplay = useCallback(() => {
    if (replayIntervalRef.current) { clearInterval(replayIntervalRef.current); replayIntervalRef.current = null; }
    setReplay(null);
  }, []);

  // ── 日期切换 ──
  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
    // 今天不带 date 参数，保持 URL 干净
    setSearchParams(newDate === todayBJ ? {} : { date: newDate }, { replace: true });
    setSelectedFlight(null);
    stopReplay();
    setLoading(true);
  }, [todayBJ, setSearchParams, stopReplay]);

  // ── 回放：单段 ──
  const startSingleReplay = useCallback(async (flight, ac) => {
    if (!flight.atd || !flight.ata) return;
    setReplay({
      mode: "single", icao24: ac.icao24, color: ac.color,
      registration: ac.registration, routeLabel: `${flight.departure_airport?.iata} → ${flight.arrival_airport?.iata}`,
      stopovers: [], legs: [flight],
      points: [], frameIndex: 0, playing: false, speed: 1, loadingTrack: true,
    });
    try {
      const resp = await screenService.getFlightTrackHistory(ac.icao24, flight.atd, flight.ata);
      const pts = resp?.data?.points || [];
      if (pts.length < 2) { alert("暂无足够的轨迹数据"); setReplay(null); return; }
      setReplay((p) => p ? { ...p, points: pts, frameIndex: 0, loadingTrack: false } : null);
    } catch (err) { console.error("[Replay] single failed:", err); setReplay(null); }
  }, []);

  // ── 回放：整段 rotation ──
  const startRotationReplay = useCallback(async (flight, ac) => {
    if (!flight.rotation_no || !ac.registration || !ac.icao24) return;
    setReplay({
      mode: "rotation", icao24: ac.icao24, color: ac.color,
      registration: ac.registration, routeLabel: "加载中...",
      stopovers: [], legs: [],
      points: [], frameIndex: 0, playing: false, speed: 1, loadingTrack: true,
    });
    try {
      const rotResp = await screenService.getRotationLegs(flight.rotation_no, ac.registration);
      const rotData = rotResp?.data || {};
      const completedLegs = (rotData.legs || []).filter((l) => l.segment_type === "completed" && l.atd && l.ata);
      if (completedLegs.length === 0) { alert("该 rotation 暂无已完成航段"); setReplay(null); return; }

      const firstAtd = completedLegs[0].atd;
      const lastAta = completedLegs[completedLegs.length - 1].ata;
      const routeLabel = completedLegs.map((l) => l.departure_airport?.iata).join(" → ") + " → " + completedLegs[completedLegs.length - 1].arrival_airport?.iata;

      const trackResp = await screenService.getFlightTrackHistory(ac.icao24, firstAtd, lastAta);
      const pts = trackResp?.data?.points || [];
      if (pts.length < 2) { alert("暂无足够的轨迹数据"); setReplay(null); return; }

      setReplay((p) => p ? {
        ...p, mode: "rotation", routeLabel,
        stopovers: rotData.stopovers || [], legs: completedLegs,
        points: pts, frameIndex: 0, loadingTrack: false,
      } : null);
    } catch (err) { console.error("[Replay] rotation failed:", err); setReplay(null); }
  }, []);

  // ── 播放控制 ──
  const togglePlay = useCallback(() => setReplay((p) => p ? { ...p, playing: !p.playing } : p), []);
  const cycleSpeed = useCallback(() => setReplay((p) => {
    if (!p) return p;
    const idx = SPEED_OPTIONS.indexOf(p.speed);
    return { ...p, speed: SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length] };
  }), []);

  useEffect(() => {
    if (!replay || !replay.playing || replayIntervalRef.current) return;
    const ms = Math.max(100, 1000 / replay.speed);
    replayIntervalRef.current = setInterval(() => {
      setReplay((p) => {
        if (!p || !p.playing) return p;
        const next = p.frameIndex + 1;
        if (next >= p.points.length) {
          clearInterval(replayIntervalRef.current);
          replayIntervalRef.current = null;
          return { ...p, playing: false, frameIndex: p.points.length - 1 };
        }
        return { ...p, frameIndex: next };
      });
    }, ms);
    return () => { if (replayIntervalRef.current) { clearInterval(replayIntervalRef.current); replayIntervalRef.current = null; } };
  }, [replay?.playing, replay?.speed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 回放派生数据 ──
  const replayCurrentPoint = replay?.points?.[replay.frameIndex] ?? null;
  const replayFlownPath = useMemo(() => {
    if (!replay?.points?.length) return [];
    return replay.points.slice(0, (replay.frameIndex ?? 0) + 1).map((p) => [p.latitude, p.longitude]);
  }, [replay?.points, replay?.frameIndex]);

  // 当前帧所在的航段（用于显示当前航段信息）
  const replayCurrentLeg = useMemo(() => {
    if (!replay || !replayCurrentPoint || !replay.legs?.length) return null;
    const t = dayjs(replayCurrentPoint.recorded_at);
    for (let i = replay.legs.length - 1; i >= 0; i--) {
      const leg = replay.legs[i];
      if (leg.atd && t.isAfter(dayjs(leg.atd).subtract(1, "minute"))) return leg;
    }
    return replay.legs[0];
  }, [replay, replayCurrentPoint]);

  // 经停站信息（过滤有坐标的）
  const replayStopovers = useMemo(() => {
    if (!replay?.stopovers?.length) return [];
    return replay.stopovers.filter((s) => s.latitude && s.longitude);
  }, [replay?.stopovers]);

  // 判断当前是否有多段已完成航段可做整段回放
  const canRotationReplay = useMemo(() => {
    if (!selectedFlight?.rotation_no || !selectedFlight?.icao24) return false;
    const ac = aircraftList.find((a) => a.registration === selectedFlight.registration);
    if (!ac) return false;
    const completedInRotation = ac.flights.filter((f) =>
      f.rotation_no === selectedFlight.rotation_no && f.segment_type === "completed" && f.atd && f.ata
    );
    return completedInRotation.length >= 2;
  }, [selectedFlight, aircraftList]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="flight-map-page">
        <div className="fmp-loading"><div className="loading-spinner" /><p>航班数据加载中...</p></div>
      </div>
    );
  }

  return (
    <div className="flight-map-page">
      {/* ── Header ── */}
      <header className="fmp-header">
        <h1 className="fmp-title">航班实时轨迹监控</h1>

        {/* ── 日期选择器 ── */}
        <div className="fmp-date-bar">
          <button
            className={`fmp-date-nav-btn`}
            title="前一天"
            onClick={() => handleDateChange(dayjs(selectedDate).subtract(1, "day").format("YYYY-MM-DD"))}>
            ‹
          </button>
          <input
            type="date"
            className="fmp-date-input"
            value={selectedDate}
            max={todayBJ}
            onChange={(e) => e.target.value && handleDateChange(e.target.value)}
          />
          <button
            className={`fmp-date-nav-btn`}
            title="后一天"
            disabled={isToday}
            onClick={() => {
              const next = dayjs(selectedDate).add(1, "day").format("YYYY-MM-DD");
              if (next <= todayBJ) handleDateChange(next);
            }}>
            ›
          </button>
          {!isToday && (
            <button className="fmp-date-today-btn" onClick={() => handleDateChange(todayBJ)} title="回到今天">
              今天
            </button>
          )}
          <span className={`fmp-date-mode ${isToday ? "live" : "history"}`}>
            {isToday ? "● 实时" : "◎ 历史"}
          </span>
        </div>

        <div className="fmp-filter-bar">
          {FILTER_OPTIONS.map((opt) => {
            const count = opt.value === "all" ? aircraftList.reduce((n, ac) => n + ac.flights.length, 0)
              : opt.value === "in_flight" ? inFlightCount : opt.value === "completed" ? completedCount : upcomingCount;
            return (
              <button key={opt.value} className={`fmp-filter-btn ${segmentFilter === opt.value ? "active" : ""} filter-${opt.value}`}
                onClick={() => setSegmentFilter(opt.value)}>
                <span className="fmp-filter-icon">{opt.icon}</span><span>{opt.label}</span>
                <span className="fmp-filter-badge">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="fmp-status">
          <span className="fmp-stat">机队 <strong>{aircraftList.length}</strong></span>
          <span className="fmp-stat">在飞 <strong>{inFlightCount}</strong></span>
          <span className="fmp-time">{currentTime.format("HH:mm:ss")}</span>
          {lastUpdate && <span className="fmp-update">更新 {lastUpdate.format("HH:mm:ss")}</span>}
        </div>
      </header>

      {/* ── Map ── */}
      <div className="fmp-map-container">
        <MapContainer center={[30, 60]} zoom={3} minZoom={2} maxZoom={12}
          zoomControl={false} worldCopyJump style={{ width: "100%", height: "100%" }}>
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>' subdomains="abcd" />

          {!replay && <GeodesicLinesLayer aircraft={filteredAircraftList} />}
          {replay?.points?.length > 0 && <ReplayFitBounds points={replay.points} />}

          {airports.map((apt) => (
            <Marker key={apt.iata} position={[apt.latitude, apt.longitude]} icon={createAirportIcon()}>
              <Tooltip permanent direction="top" offset={[0, -8]} className="airport-tooltip">{apt.iata}</Tooltip>
            </Marker>
          ))}

          {!replay && planes.map((p) => (
            <Marker key={p.key} position={[p.position.latitude, p.position.longitude]}
              icon={createPlaneIcon(p.position.heading, p.color, true)}
              eventHandlers={{ click: () => handlePlaneClick(p) }}>
              <Tooltip direction="right" offset={[16, 0]} className="plane-tooltip">
                <div>{p.flightNumber}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{p.registration}</div>
              </Tooltip>
            </Marker>
          ))}

          {/* ── 回放: 已飞轨迹线 ── */}
          {replay && replayFlownPath.length > 1 && (
            <Polyline positions={replayFlownPath} pathOptions={{ color: replay.color, weight: 3, opacity: 0.9 }} />
          )}

          {/* ── 回放: 飞机图标 ── */}
          {replay && replayCurrentPoint && (
            <Marker position={[replayCurrentPoint.latitude, replayCurrentPoint.longitude]}
              icon={createReplayPlaneIcon(replayCurrentPoint.heading, replay.color)}>
              <Tooltip direction="right" offset={[18, 0]} className="plane-tooltip" permanent>
                <div style={{ color: replay.color }}>
                  {replayCurrentLeg?.flight_number || replay.registration}
                </div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>
                  {replayCurrentPoint.baro_altitude ? `${Math.round(replayCurrentPoint.baro_altitude).toLocaleString()}m` : "地面"}
                  {replayCurrentPoint.speed_kmh ? ` · ${Math.round(replayCurrentPoint.speed_kmh)}km/h` : ""}
                </div>
              </Tooltip>
            </Marker>
          )}

          {/* ── 回放: 经停站菱形标注 ── */}
          {replay && replayStopovers.map((s) => (
            <Marker key={`stop-${s.iata}`} position={[s.latitude, s.longitude]} icon={createStopoverIcon()}>
              <Tooltip permanent direction="top" offset={[0, -12]} className="stopover-tooltip">
                <div className="stopover-label">{s.iata}</div>
                {s.ground_minutes > 0 && (
                  <div className="stopover-ground">⏱ {formatGround(s.ground_minutes)}</div>
                )}
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>

        {replay?.loadingTrack && (
          <div className="fmp-replay-loading"><div className="loading-spinner" /><span>加载轨迹数据...</span></div>
        )}
      </div>

      {/* ── 回放控制栏 ── */}
      {replay && !replay.loadingTrack && (
        <div className="fmp-replay-bar">
          <div className="fmp-replay-info">
            <span className="fmp-replay-fn" style={{ color: replay.color }}>
              {replay.mode === "rotation" ? "⏮ 整段回放" : "⏮ 回放"}: {replay.registration}
            </span>
            <span className="fmp-replay-route">{replay.routeLabel}</span>
            <span className="fmp-replay-time">
              {replayCurrentPoint ? dayjs(replayCurrentPoint.recorded_at).utc().format("HH:mm:ss[Z]") : "--"}
            </span>
            {replayCurrentLeg && (
              <span className="fmp-replay-leg-tag">
                {replayCurrentLeg.departure_airport?.iata}→{replayCurrentLeg.arrival_airport?.iata}
              </span>
            )}
            <span className="fmp-replay-pts">{replay.frameIndex + 1} / {replay.points.length}</span>
          </div>

          <input type="range" className="fmp-replay-slider" min={0}
            max={Math.max(0, replay.points.length - 1)} value={replay.frameIndex}
            onChange={(e) => {
              const idx = parseInt(e.target.value, 10);
              setReplay((p) => p ? { ...p, frameIndex: idx, playing: false } : p);
            }} />

          <div className="fmp-replay-controls">
            <button className="fmp-ctrl-btn" onClick={() => setReplay((p) => p ? { ...p, frameIndex: 0, playing: false } : p)} title="回到开头">⏮</button>
            <button className="fmp-ctrl-btn fmp-ctrl-play" onClick={togglePlay}>{replay.playing ? "⏸" : "▶"}</button>
            <button className="fmp-ctrl-btn fmp-ctrl-speed" onClick={cycleSpeed} title="切换速度">{replay.speed}x</button>
            <button className="fmp-ctrl-btn fmp-ctrl-close" onClick={stopReplay} title="退出回放">✕</button>
          </div>
        </div>
      )}

      {/* ── Bottom panel ── */}
      <div className="fmp-bottom-panel">
        <div className="fmp-fleet-list">
          {aircraftList.map((ac) => {
            const isFlying = ac.flights.some((f) => f.segment_type === "in_flight");
            const isSelected = selectedFlight?.registration === ac.registration;
            const isDimmed = segmentFilter !== "all" && !ac.flights.some((f) => f.segment_type === segmentFilter);
            return (
              <div key={ac.registration}
                className={`fmp-fleet-item ${isFlying ? "flying" : ""} ${isSelected ? "selected" : ""} ${isDimmed ? "dimmed" : ""}`}
                style={{ borderColor: isDimmed ? "rgba(255,255,255,0.1)" : ac.color }}
                onClick={() => handleAircraftSelect(ac)}>
                <div className="fmp-fleet-reg" style={{ color: ac.color }}>{ac.registration}</div>
                <div className="fmp-fleet-type">{ac.aircraft_type}</div>
                <div className={`fmp-fleet-status ${isFlying ? "active" : ""}`}>{isFlying ? "IN FLIGHT" : "GROUND"}</div>
              </div>
            );
          })}
        </div>

        {selectedFlight && (
          <div className="fmp-detail-panel">
            <div className="fmp-detail-header">
              <span className="fmp-detail-fn" style={{ color: selectedFlight.color }}>{selectedFlight.flight_number}</span>
              <span className="fmp-detail-reg">{selectedFlight.registration} · {selectedFlight.aircraft_type}</span>
              <button className="fmp-detail-close" onClick={() => setSelectedFlight(null)}>&times;</button>
            </div>
            <div className="fmp-detail-route">
              <span>{selectedFlight.departure_airport?.iata}</span>
              <span className="fmp-route-arrow">
                {selectedFlight.segment_type === "completed" ? "───▶" : selectedFlight.segment_type === "in_flight" ? "──✈▶" : "╌╌╌▶"}
              </span>
              <span>{selectedFlight.arrival_airport?.iata}</span>
            </div>
            <div className="fmp-detail-body">
              <div className="fmp-detail-grid">
                <div><label>状态</label><span className={`fmp-status-tag ${selectedFlight.segment_type}`}>{selectedFlight.status}</span></div>
                <div><label>STD</label><span>{selectedFlight.std ? dayjs(selectedFlight.std).utc().format("HH:mm[Z]") : "--"}</span></div>
                <div><label>STA</label><span>{selectedFlight.sta ? dayjs(selectedFlight.sta).utc().format("HH:mm[Z]") : "--"}</span></div>
                {selectedFlight.atd && <div><label>ATD</label><span>{dayjs(selectedFlight.atd).utc().format("HH:mm[Z]")}</span></div>}
                {selectedFlight.ata && <div><label>ATA</label><span>{dayjs(selectedFlight.ata).utc().format("HH:mm[Z]")}</span></div>}
                {selectedFlight.current_position && (<>
                  <div><label>高度</label><span>{selectedFlight.current_position.altitude ? `${Math.round(selectedFlight.current_position.altitude).toLocaleString()}m` : "--"}</span></div>
                  <div><label>速度</label><span>{selectedFlight.current_position.speed_kmh ? `${Math.round(selectedFlight.current_position.speed_kmh)} km/h` : "--"}</span></div>
                  <div><label>航向</label><span>{selectedFlight.current_position.heading ? `${Math.round(selectedFlight.current_position.heading)}°` : "--"}</span></div>
                </>)}
              </div>

              {/* 回放按钮区 */}
              {selectedFlight.segment_type === "completed" && selectedFlight.atd && selectedFlight.ata && selectedFlight.icao24 && (
                <div className="fmp-replay-btns">
                  <button className="fmp-replay-btn"
                    style={{ borderColor: selectedFlight.color, color: selectedFlight.color }}
                    onClick={() => startSingleReplay(selectedFlight, {
                      icao24: selectedFlight.icao24, color: selectedFlight.color,
                      registration: selectedFlight.registration, aircraft_type: selectedFlight.aircraft_type,
                    })}>
                    ⏵ 本段回放
                  </button>
                  {canRotationReplay && (
                    <button className="fmp-replay-btn fmp-replay-btn-rotation"
                      style={{ borderColor: selectedFlight.color, color: selectedFlight.color }}
                      onClick={() => startRotationReplay(selectedFlight, {
                        icao24: selectedFlight.icao24, color: selectedFlight.color,
                        registration: selectedFlight.registration, aircraft_type: selectedFlight.aircraft_type,
                      })}>
                      ⏵ 整段回放
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightMapPage;
